<template>
    <div class="match-players">
        <section v-if="mvp && (mvpPlayer || otherPlayer)" class="cl-section match-players__best">
            <mvp v-if="mvpPlayer" :player="mvpPlayer" :team="mvpTeam" :live="live" />
            <mvp v-if="otherPlayer" :player="otherPlayer" :team="otherTeam" secondary />
        </section>

        <slot name="compare" />

        <!-- the best of each team per category -->
        <section class="cl-section">
            <section-heading title="Κορυφαίοι ανά κατηγορία" icon="mdi-podium" />
            <div class="leaders">
                <div class="leaders__teams">
                    <span v-for="(team, side) in teams" :key="side" class="leaders__team" :class="`leaders__team--${side}`">
                        <span class="cl-logo leaders__team-logo"><img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" alt=""></span>
                        <span class="leaders__team-name">{{ team.name }}</span>
                    </span>
                </div>
                <div v-for="row in leaders" :key="row.value" class="leaders__row">
                    <router-link
                        v-for="(side, index) in [row.team1, row.team2]"
                        :key="index"
                        :to="side.player ? { path: `/Player/${side.player.id}` } : ''"
                        class="leaders__player"
                        :class="[`leaders__player--${index}`, { 'leaders__player--none': !side.player }]"
                        :style="{ order: index === 0 ? 0 : 2 }"
                    >
                        <img :src="side.player && side.player.image ? `${assetsUrl}/${side.player.image}` : defaultPlayerImg" alt="" class="leaders__photo" @error="$event.target.src = defaultPlayerImg">
                        <span class="leaders__name">{{ side.player ? `${side.player.name || ''} ${side.player.surname || ''}`.trim() : '–' }}</span>
                    </router-link>
                    <span class="leaders__figures" :style="{ order: 1 }">
                        <span class="leaders__value" :class="{ 'leaders__value--lead': row.lead === 1 }">{{ row.display1 }}</span>
                        <span class="leaders__label">{{ row.text }}</span>
                        <span class="leaders__value" :class="{ 'leaders__value--lead': row.lead === 2 }">{{ row.display2 }}</span>
                    </span>
                </div>
            </div>
        </section>

        <section class="cl-section">
            <match-stats :match="match" />
        </section>
    </div>
</template>

<script>
    import MatchStats from '@/components/MatchStats'
    import Mvp from '@/components/Mvp'
    import SectionHeading from '@/components/SectionHeading'

    const CATEGORIES = [
        { text: 'EFF', value: 'pir' },
        { text: 'PTS', value: 'points' },
        { text: 'TR', value: 'rebounds' },
        { text: 'OR', value: 'offensiveRebounds' },
        { text: 'DR', value: 'defensiveRebounds' },
        { text: 'AST', value: 'assists' },
        { text: 'STL', value: 'steals' },
        { text: 'BLK', value: 'blockedShots' },
        { text: '2P%', value: 'two', shot: ['twoPointsMade', 'twoPointAttempts'] },
        { text: '3P%', value: 'three', shot: ['threePointsMade', 'threePointAttempts'] },
        { text: 'FT%', value: 'ft', shot: ['freeThrowsMade', 'freeThrowAttempts'] }
    ]

    // the efficiency of a line, as the league counts it
    function efficiency(s) {
        return (s.points + s.rebounds + s.assists + s.steals + s.blockedShots) -
            ((s.twoPointAttempts + s.threePointAttempts - s.twoPointsMade - s.threePointsMade) +
                (s.freeThrowAttempts - s.freeThrowsMade) + s.turnovers + s.personalFouls + s.technicalFouls)
    }

    // the best player of a team in one category
    function leader(team, category) {
        let best = null
        let bestValue = 0
        let bestLine = null
        ;((team && team.players) || []).forEach(player => {
            const line = player.stats && player.stats[0]
            if (!line) return
            let value
            if (category.shot) {
                const [made, att] = category.shot
                value = line[att] ? line[made] / line[att] : 0
            } else {
                value = line[category.value] || 0
            }
            if (value > bestValue) {
                best = player
                bestValue = value
                bestLine = line
            }
        })
        let display = category.shot ? '0%' : 0
        if (best) {
            if (category.shot) {
                const [made, att] = category.shot
                display = `${Math.round(bestValue * 100)}% (${bestLine[made]}/${bestLine[att]})`
            } else {
                display = bestValue
            }
        }
        return { player: best, value: bestValue, display }
    }

    export default {
        components: { MatchStats, Mvp, SectionHeading },
        props: {
            match: {
                type: Object,
                default: null
            },
            mvp: {
                type: Boolean,
                default: true
            },
            // while live the MVP is the best so far
            live: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                defaultPlayerImg: require('@/assets/default_player.png'),
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            teams() {
                return [this.match.team1 || {}, this.match.team2 || {}]
            },
            leaders() {
                return CATEGORIES.map(category => {
                    const team1 = leader(this.match.team1, category)
                    const team2 = leader(this.match.team2, category)
                    return {
                        text: category.text,
                        value: category.value,
                        team1,
                        team2,
                        display1: team1.display,
                        display2: team2.display,
                        lead: team1.value === team2.value ? 0 : (team1.value > team2.value ? 1 : 2)
                    }
                })
            },
            // the winning team, or the second team on a draw like before
            mvpTeam() {
                return this.match.team1Score > this.match.team2Score ? this.match.team1 : this.match.team2
            },
            otherTeam() {
                return this.mvpTeam === this.match.team1 ? this.match.team2 : this.match.team1
            },
            mvpPlayer() {
                return this.bestOf(this.mvpTeam)
            },
            otherPlayer() {
                return this.bestOf(this.otherTeam)
            }
        },
        watch: {
            match() {
                this.applyEfficiency()
            }
        },
        created() {
            this.applyEfficiency()
        },
        methods: {
            // the best efficiency of a team
            bestOf(team) {
                let best = null
                ;((team && team.players) || []).forEach(player => {
                    const line = player.stats && player.stats[0]
                    if (line && (!best || line.pir > best.stats[0].pir)) {
                        best = player
                    }
                })
                return best
            },
            applyEfficiency() {
                [this.match.team1, this.match.team2].forEach(team => {
                    ((team && team.players) || []).forEach(player => {
                        if (player.stats && player.stats[0]) {
                            player.stats[0].pir = efficiency(player.stats[0])
                        }
                    })
                })
            }
        }
    }
</script>

<style scoped>
.match-players__best {
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
    align-items: center;
    gap: 18px;
}

@media (max-width: 1100px) {
    .match-players__best {
        grid-template-columns: 1fr;
    }
}

.leaders {
    padding: 6px 20px 10px;
    border-radius: 12px;
    background: #232323;
    color: #fff;
    box-shadow: 0 8px 24px rgba(35, 35, 35, 0.12);
    font-family: Roboto, Arial, sans-serif;
    font-variant-numeric: tabular-nums;
}

.leaders__teams {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0 12px;
    border-bottom: 2px solid rgba(3, 229, 183, 0.35);
}

.leaders__team {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.leaders__team--1 {
    flex-direction: row-reverse;
    text-align: right;
}

.leaders__team-logo {
    width: 36px;
    height: 36px;
}

.leaders__team-name {
    overflow: hidden;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.2rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.leaders__row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(220px, auto) minmax(0, 1fr);
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.leaders__row:last-child {
    border-bottom: 0;
}

.leaders__player {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    color: #fff !important;
    text-decoration: none;
}

.leaders__player--1 {
    flex-direction: row-reverse;
    text-align: right;
}

.leaders__player--none {
    pointer-events: none;
    color: rgba(255, 255, 255, 0.35) !important;
}

.leaders__player:hover .leaders__name {
    color: #03e5b7;
}

.leaders__photo {
    flex: 0 0 auto;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: #3a3a3a;
    object-fit: cover;
    object-position: top center;
}

.leaders__name {
    overflow: hidden;
    font-size: 0.9rem;
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.leaders__figures {
    display: grid;
    grid-template-columns: 1fr 48px 1fr;
    align-items: center;
}

.leaders__value {
    font-size: 1.05rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.75);
    white-space: nowrap;
}

.leaders__value:first-child {
    text-align: right;
}

.leaders__value--lead {
    color: #03e5b7;
}

.leaders__label {
    text-align: center;
    color: #03e5b7;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.08em;
}

@media (max-width: 700px) {
    .leaders {
        padding: 4px 10px 8px;
    }

    .leaders__team-logo {
        width: 28px;
        height: 28px;
    }

    .leaders__team-name {
        font-size: 1rem;
    }

    .leaders__row {
        grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
        gap: 6px;
    }

    .leaders__player {
        flex-direction: column;
        gap: 4px;
        text-align: center;
    }

    .leaders__player--1 {
        flex-direction: column;
        text-align: center;
    }

    .leaders__photo {
        width: 34px;
        height: 34px;
    }

    .leaders__name {
        max-width: 100%;
        font-size: 0.72rem;
    }

    .leaders__figures {
        grid-template-columns: auto 38px auto;
    }

    .leaders__value {
        font-size: 0.85rem;
    }

    .leaders__label {
        font-size: 0.64rem;
    }
}
</style>
